import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { typeCalendar, typeCustomFields, typeCustomKey } from "./HomeApi";
import { AvailabilitySlot } from "./utils/generateStartTimes";

export const OUTLETS = [
  "Suntec City",
  "Bugis+",
  "Orchard Cineleisure",
  "Somerset",
  "Bugis Cube",
  "Yishun",
  // "Thomson Plaza",
  "Chinatown",
  // "Downtown East",
  "Toa Payoh",
  "Pasir Ris Mall",
  "The Seletar Mall"
] as const;

export const OUTLET_ORGANISATIONS = {
  [`${OUTLETS[0]}`]: {
    organisationUuid: process.env.REACT_APP_HAVEFUN_SUNTEC_ORGANISATION_UUID,
    menuRate: "https://havefunkaraoke.com/suntec-city-rates-menu/",
  },
  [`${OUTLETS[1]}`]: {
    organisationUuid:
      process.env.REACT_APP_HAVEFUN_BUGIS_PLUS_ORGANISATION_UUID,
    menuRate: "https://havefunkaraoke.com/bugis-rates-menu/",
  },
  [`${OUTLETS[2]}`]: {
    organisationUuid:
      process.env.REACT_APP_HAVEFUN_CINELEISURE_ORGANISATION_UUID,
    menuRate: "https://havefunkaraoke.com/cineleisure-orchard-rates-menu/",
  },
  [`${OUTLETS[3]}`]: {
    organisationUuid: process.env.REACT_APP_HAVEFUN_SOMERSET_ORGANISATION_UUID,
    menuRate: "https://havefunkaraoke.com/313somerset-rates-menu/",
  },
  [`${OUTLETS[4]}`]: {
    organisationUuid:
      process.env.REACT_APP_HAVEFUN_BUGIS_CUBE_ORGANISATION_UUID,
    menuRate: "https://havefunkaraoke.com/bugis-cube-rates-menu/",
  },
  [`${OUTLETS[5]}`]: {
    organisationUuid: process.env.REACT_APP_HAVEFUN_YISHUN_ORGANISATION_UUID,
    menuRate: "https://havefunkaraoke.com/yishun-rates-menu/",
  },
  // [`${OUTLETS[6]}`]: {
  //   organisationUuid: process.env.REACT_APP_HAVEFUN_THOMSON_ORGANISATION_UUID,
  //   menuRate: "https://havefunkaraoke.com/thomson-plaza-rates-menu/",
  // },
  [`${OUTLETS[6]}`]: {
    organisationUuid: process.env.REACT_APP_HAVEFUN_CHINATOWN_ORGANISATION_UUID,
    menuRate: "https://havefunkaraoke.com/lucky-chinatown-rates-menu/",
  },
  // [`${OUTLETS[7]}`]: {
  //   organisationUuid:
  //     process.env.REACT_APP_HAVEFUN_DOWNTOWN_EAST_ORGANISATION_UUID,
  //   menuRate: "https://havefunkaraoke.com/downtown-east-rates-menu/",
  // },
  [`${OUTLETS[7]}`]: {
    organisationUuid: process.env.REACT_APP_HAVEFUN_TOA_PAYOH_ORGANISATION_UUID,
    menuRate: "https://havefunkaraoke.com/toa-payoh-rates-menu/",
  },
  [`${OUTLETS[8]}`]: {
    organisationUuid: process.env.REACT_APP_HAVEFUN_PASIR_RIS_ORGANISATION_UUID,
    menuRate: "https://havefunkaraoke.com/pasir-ris-mall-rates-menu/",
  },
  [`${OUTLETS[9]}`]: {
    organisationUuid: process.env.REACT_APP_HAVEFUN_SELETAR_MALL_ORGANISATION_UUID,
    menuRate: "https://havefunkaraoke.com/the-seletar-mall-rates-menu/",
  },
};

export const ROOM_TYPES = [
  {
    label: "小房 Exclusive Room",
    exabloomLabel: "小房 Exclusive Room",
    maxPax: 6,
    visible: true,
  },
  {
    label: "中房 Exclusive+ Room",
    exabloomLabel: "中房 Exclusive+ Room",
    maxPax: 8,
    visible: true,
  },
  {
    label: "大房 Deluxe Room",
    exabloomLabel: "大房 Deluxe Room",
    maxPax: 15,
    visible: true,
  },
  {
    label: "VIP包房 VIP ROOM",
    exabloomLabel: "VIP包房 VIP ROOM",
    maxPax: 40,
    visible: false,
  },
] as const;

export type typeOutlets = (typeof OUTLETS)[number];

export type typeRooms = (typeof ROOM_TYPES)[number];

export const CUSTOM_KEY_TYPES = [
  "TEXT",
  "SINGLE_SELECT",
  "MULTI_SELECT",
  "TIME",
  "DATE",
  "DATE_TIME",
  "NUMBER",
] as const;

export type typeCustomKeyTypes = (typeof CUSTOM_KEY_TYPES)[number];

export const DEFAULT_CREATE_BOOKING_FORM = {
  outlet: OUTLETS[0] as typeOutlets,
  pax: 1,
  roomType: ROOM_TYPES[0].label as string,
  duration: 2,
  name: "",
  date: null as Date | null,
  phone: "+65",
  dateTime: "",
  calendarUuid: "",
};

export type typeBookingForm = typeof DEFAULT_CREATE_BOOKING_FORM;

export const DEFAULT_SELECTED_CALENDAR = {
  uuid: "",
  name: "",
  interval: 15 * 60 * 1000,
  order: 0,
  color: "#FFFFFF",
  openHours: [],
  customFields: [],
} as typeCalendar;

export type typeAvailableSlot = {
  calendarUuid: string;
  calendarName: string;
  startTime: string;
  endTime: string;
};

interface Appointment {
  uuid: string;
  startTime: string;
  endTime: string;
  calendar: {
    uuid: string;
    name: string;
  };
}

const initialState = {
  createBookingForm: DEFAULT_CREATE_BOOKING_FORM,
  calendars: [] as typeCalendar[],
  appointments: [] as Appointment[],
  availableSlots: [] as typeAvailableSlot[],
  timeslotsWithRemovedClashes: [] as AvailabilitySlot[],
  customKeys: [] as typeCustomKey[],
  submitDialog: {
    open: false,
  },
  customFields: [] as typeCustomFields,
  isLoadingSlots: false,
};

export const homeSlice = createSlice({
  name: "home",
  initialState,
  reducers: {
    setCreateBookingForm: (
      state,
      action: PayloadAction<typeof initialState.createBookingForm>,
    ) => {
      state.createBookingForm = action.payload;
    },
    setCalendars: (
      state,
      action: PayloadAction<typeof initialState.calendars>,
    ) => {
      state.calendars = action.payload;
    },
    setAppointments: (state, action: PayloadAction<Appointment[]>) => {
      state.appointments = action.payload;
    },
    setAvailableSlots: (
      state,
      action: PayloadAction<typeof initialState.availableSlots>,
    ) => {
      state.availableSlots = action.payload;
    },
    setTimeslotsWithRemovedClashes: (
      state,
      action: PayloadAction<AvailabilitySlot[]>,
    ) => {
      state.timeslotsWithRemovedClashes = action.payload;
    },
    setCustomKeys: (
      state,
      action: PayloadAction<typeof initialState.customKeys>,
    ) => {
      state.customKeys = action.payload;
    },
    setSubmitDialog: (
      state,
      action: PayloadAction<typeof initialState.submitDialog>,
    ) => {
      state.submitDialog = action.payload;
    },
    setCustomFields: (
      state,
      action: PayloadAction<typeof initialState.customFields>,
    ) => {
      state.customFields = action.payload;
    },
    setIsLoadingSlots: (
      state,
      action: PayloadAction<typeof initialState.isLoadingSlots>,
    ) => {
      state.isLoadingSlots = action.payload;
    },
    resetApp: () => initialState,
  },
});

export const {
  setCreateBookingForm,
  setCalendars,
  setAppointments,
  setAvailableSlots,
  setTimeslotsWithRemovedClashes,
  setCustomKeys,
  setSubmitDialog,
  setCustomFields,
  setIsLoadingSlots,
  resetApp,
} = homeSlice.actions;

export default homeSlice.reducer;

export const PUBLIC_HOLIDAYS = [
  // 2024 Holidays
  "01/01/2024", // New Year's Day
  "10/02/2024", // Chinese New Year
  "11/02/2024", // Chinese New Year Holiday
  "12/02/2024", // Chinese New Year Holiday
  "29/03/2024", // Good Friday
  "10/04/2024", // Hari Raya Puasa
  "01/05/2024", // Labour Day
  "22/05/2024", // Vesak Day
  "17/06/2024", // Hari Raya Haji
  "09/08/2024", // National Day
  "31/10/2024", // Deepavali
  "25/12/2024", // Christmas Day

  // 2025 Holidays
  "01/01/2025", // New Year's Day
  "29/01/2025", // Chinese New Year
  "30/01/2025", // Chinese New Year Holiday
  "31/03/2025", // Hari Raya Puasa
  "18/04/2025", // Good Friday
  "01/05/2025", // Labour Day
  "12/05/2025", // Vesak Day
  "06/06/2025", // Hari Raya Haji
  "09/08/2025", // National Day
  "20/10/2025", // Deepavali
  "25/12/2025", // Christmas Day

  // 2026 Holidays
  "01/01/2026", // New Year's Day
  "17/02/2026", // Chinese New Year
  "18/02/2026", // Chinese New Year Holiday
  "20/03/2026", // Hari Raya Puasa
  "03/04/2026", // Good Friday
  "01/05/2026", // Labour Day
  "27/05/2026", // Hari Raya Haji
  "31/05/2026", // Vesak Day
  "09/08/2026", // National Day
  "10/08/2026", // National Day Holiday
  "08/11/2026", // Deepavali
  "09/11/2026", // Deepavali Holiday
  "25/12/2026", // Christmas Day
];
